/**
 * LearnedWeightsWidget — the weights the Debate Council is ACTUALLY applying.
 *
 * Reads GET /api/feedback/weights (the learned_weights table consumed by
 * debate_council._weighted_aggregate at runtime). Honesty rule: an entry only
 * influences the council when it carries per-seat keys (quant, skeptic, scout,
 * analyst, contrarian, judge). An aggregate-only entry ("system") or an empty
 * table means the council is running on STATIC default weights — the widget
 * says so explicitly instead of implying adaptive weighting is active.
 */
import { Scale, AlertTriangle } from "lucide-react";

import { LoadingState } from "@/components/loading-state";
import { Panel } from "@/components/panel";
import { QueryErrorState } from "@/components/query-error-state";
import {
  learnedWeightsActive,
  useFeedbackWeights,
} from "@/sdk/hooks/use-alpha";
import type { LearnedCategoryWeights } from "@/sdk/hooks/use-alpha";

function CategoryRow({
  category,
  entry,
}: {
  category: string;
  entry: LearnedCategoryWeights;
}) {
  const active = learnedWeightsActive(entry);
  const seatWeights = Object.entries(entry.weights ?? {}).sort(
    (a, b) => b[1] - a[1],
  );

  return (
    <div className="rounded border border-white/5 px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-white/80 capitalize">
          {category}
        </span>
        <span
          className={`rounded px-1.5 py-0.5 text-[0.65rem] font-semibold ${
            active
              ? "bg-emerald-500/20 text-emerald-300"
              : "bg-amber-500/20 text-amber-300"
          }`}
        >
          {active ? "learned weights active" : "static fallback"}
        </span>
      </div>
      <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
        {seatWeights.map(([seat, weight]) => (
          <span key={seat} className="text-[0.7rem] font-mono text-white/60">
            {seat}: {typeof weight === "number" ? weight.toFixed(3) : String(weight)}
          </span>
        ))}
      </div>
      <div className="mt-1 flex items-center justify-between text-[0.65rem] text-white/30">
        <span>
          {entry.sample_size} resolution{entry.sample_size === 1 ? "" : "s"}
          {entry.avg_brier !== null ? ` · avg Brier ${entry.avg_brier.toFixed(3)}` : ""}
        </span>
        <span>updated {entry.last_updated?.slice(0, 10) ?? "—"}</span>
      </div>
    </div>
  );
}

export function LearnedWeightsWidget() {
  const query = useFeedbackWeights();
  const weights = query.data?.weights ?? {};
  const categories = Object.entries(weights);
  const anyActive = categories.some(([, entry]) => learnedWeightsActive(entry));

  return (
    <Panel
      title="Council Weights"
      description="Learned per-seat weights the Debate Council applies"
    >
      {query.isError ? (
        <QueryErrorState
          what="learned weights"
          onRetry={() => void query.refetch()}
        />
      ) : query.isLoading ? (
        <LoadingState text="Loading learned weights..." />
      ) : categories.length === 0 ? (
        <div className="py-6 text-center">
          <Scale className="mx-auto h-8 w-8 text-white/20" />
          <p className="mt-2 text-sm text-white/40">
            No learned weights recorded yet.
          </p>
          <p className="text-xs text-white/25">
            The council is using its static default weights until enough live
            resolutions accumulate (≥3 per category).
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {!anyActive ? (
            <div className="flex items-start gap-2 rounded border border-amber-500/20 bg-amber-500/5 px-3 py-2">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400" />
              <p className="text-[0.7rem] text-amber-300/90">
                Recorded weights carry no per-seat keys, so the council is
                still aggregating with STATIC default weights. Adaptive
                weighting is not in effect.
              </p>
            </div>
          ) : null}
          {categories.map(([category, entry]) => (
            <CategoryRow key={category} category={category} entry={entry} />
          ))}
        </div>
      )}
    </Panel>
  );
}
